/**
 * Calendar Mock Data
 * Includes tasks, sprints, meetings, team availability, and recurring events
 */

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'task' | 'sprint' | 'meeting' | 'availability' | 'milestone' | 'recurring';
  status?: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee?: string;
  description?: string;
  location?: string;
  attendees?: string[];
  recurrence?: 'daily' | 'weekly' | 'monthly';
  color?: string;
  isAllDay?: boolean;
  googleCalendarId?: string;
}

// Helper function to create dates
const createDate = (daysOffset: number, hours: number = 9, minutes: number = 0): Date => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Task Deadlines and Milestones
export const taskEvents: CalendarEvent[] = [
  {
    id: 'task-1',
    title: 'Complete Sprint Planning',
    start: createDate(2, 14, 0),
    end: createDate(2, 16, 0),
    type: 'task',
    status: 'in-progress',
    priority: 'high',
    assignee: 'John Smith',
    description: 'Review backlog and assign story points',
    color: '#4A90E2',
  },
  {
    id: 'task-2',
    title: 'Code Review: Authentication Module',
    start: createDate(3, 10, 0),
    end: createDate(3, 11, 30),
    type: 'task',
    status: 'pending',
    priority: 'medium',
    assignee: 'Sarah Johnson',
    description: 'Review JWT implementation and security',
    color: '#4A90E2',
  },
  {
    id: 'milestone-1',
    title: '🎯 MVP Release',
    start: createDate(7, 0, 0),
    end: createDate(7, 23, 59),
    type: 'milestone',
    status: 'pending',
    priority: 'urgent',
    description: 'Minimum Viable Product launch',
    color: '#FF6B6B',
    isAllDay: true,
  },
  {
    id: 'task-3',
    title: 'Deploy to Production',
    start: createDate(6, 15, 0),
    end: createDate(6, 17, 0),
    type: 'task',
    status: 'pending',
    priority: 'urgent',
    assignee: 'Bob Wilson',
    description: 'Production deployment and smoke tests',
    color: '#4A90E2',
  },
  {
    id: 'milestone-2',
    title: '🚀 Feature Freeze',
    start: createDate(5, 0, 0),
    end: createDate(5, 23, 59),
    type: 'milestone',
    status: 'pending',
    priority: 'high',
    description: 'No new features after this date',
    color: '#FFA500',
    isAllDay: true,
  },
];

// Sprint Timelines
export const sprintEvents: CalendarEvent[] = [
  {
    id: 'sprint-1',
    title: 'Sprint 1 - Core Features',
    start: createDate(-7, 0, 0),
    end: createDate(7, 23, 59),
    type: 'sprint',
    status: 'in-progress',
    description: 'Implement authentication and dashboard',
    color: '#9C27B0',
    isAllDay: true,
  },
  {
    id: 'sprint-2',
    title: 'Sprint 2 - Analytics & Reports',
    start: createDate(8, 0, 0),
    end: createDate(21, 23, 59),
    type: 'sprint',
    status: 'pending',
    description: 'Build analytics dashboard and reporting features',
    color: '#673AB7',
    isAllDay: true,
  },
];

// Team Availability
export const availabilityEvents: CalendarEvent[] = [
  {
    id: 'avail-1',
    title: 'John Smith - Out of Office',
    start: createDate(4, 0, 0),
    end: createDate(5, 23, 59),
    type: 'availability',
    status: 'pending',
    assignee: 'John Smith',
    description: 'Personal leave',
    color: '#95A5A6',
    isAllDay: true,
  },
  {
    id: 'avail-2',
    title: 'Sarah Johnson - Remote Work',
    start: createDate(1, 0, 0),
    end: createDate(3, 23, 59),
    type: 'availability',
    status: 'in-progress',
    assignee: 'Sarah Johnson',
    description: 'Working from home',
    color: '#3498DB',
    isAllDay: true,
  },
  {
    id: 'avail-3',
    title: 'Team - Holiday',
    start: createDate(10, 0, 0),
    end: createDate(10, 23, 59),
    type: 'availability',
    status: 'pending',
    description: 'Public holiday - Office closed',
    color: '#E74C3C',
    isAllDay: true,
  },
];

// Meeting Schedules
export const meetingEvents: CalendarEvent[] = [
  {
    id: 'meeting-1',
    title: 'Daily Standup',
    start: createDate(0, 9, 30),
    end: createDate(0, 10, 0),
    type: 'meeting',
    status: 'in-progress',
    description: 'Daily team sync',
    location: 'Zoom',
    attendees: ['John Smith', 'Sarah Johnson', 'Bob Wilson'],
    recurrence: 'daily',
    color: '#00C853',
  },
  {
    id: 'meeting-2',
    title: 'Sprint Planning',
    start: createDate(1, 14, 0),
    end: createDate(1, 16, 0),
    type: 'meeting',
    status: 'pending',
    description: 'Plan next sprint tasks',
    location: 'Conference Room A',
    attendees: ['John Smith', 'Sarah Johnson', 'Bob Wilson', 'Product Owner'],
    color: '#00C853',
  },
  {
    id: 'meeting-3',
    title: 'Sprint Retrospective',
    start: createDate(7, 15, 0),
    end: createDate(7, 16, 30),
    type: 'meeting',
    status: 'pending',
    description: 'Discuss what went well and improvements',
    location: 'Zoom',
    attendees: ['John Smith', 'Sarah Johnson', 'Bob Wilson'],
    color: '#00C853',
  },
  {
    id: 'meeting-4',
    title: 'Client Demo',
    start: createDate(6, 11, 0),
    end: createDate(6, 12, 0),
    type: 'meeting',
    status: 'pending',
    priority: 'high',
    description: 'Showcase MVP features to client',
    location: 'Google Meet',
    attendees: ['John Smith', 'Product Owner', 'Client Stakeholders'],
    color: '#00C853',
  },
];

// Recurring Events
export const recurringEvents: CalendarEvent[] = [
  {
    id: 'recurring-1',
    title: '🔄 Daily Standup',
    start: createDate(0, 9, 30),
    end: createDate(0, 10, 0),
    type: 'recurring',
    status: 'in-progress',
    description: 'Team daily sync-up',
    location: 'Zoom',
    recurrence: 'daily',
    color: '#26A69A',
  },
  {
    id: 'recurring-2',
    title: '🔄 Weekly Team Lunch',
    start: createDate(5, 12, 0),
    end: createDate(5, 13, 0),
    type: 'recurring',
    status: 'pending',
    description: 'Team building activity',
    location: 'Office Cafeteria',
    recurrence: 'weekly',
    color: '#26A69A',
  },
  {
    id: 'recurring-3',
    title: '🔄 Monthly All-Hands',
    start: createDate(15, 10, 0),
    end: createDate(15, 11, 0),
    type: 'recurring',
    status: 'pending',
    description: 'Company-wide updates',
    location: 'Main Hall',
    recurrence: 'monthly',
    color: '#26A69A',
  },
];

// Google Calendar Integration (simulated)
export const googleCalendarEvents: CalendarEvent[] = [
  {
    id: 'google-1',
    title: '📧 Marketing Meeting',
    start: createDate(2, 11, 0),
    end: createDate(2, 12, 0),
    type: 'meeting',
    status: 'pending',
    description: 'Synced from Google Calendar',
    location: 'Google Meet',
    googleCalendarId: 'primary',
    color: '#4285F4',
  },
  {
    id: 'google-2',
    title: '📧 Doctor Appointment',
    start: createDate(4, 14, 0),
    end: createDate(4, 15, 0),
    type: 'availability',
    status: 'pending',
    description: 'Personal event from Google Calendar',
    googleCalendarId: 'personal',
    color: '#34A853',
  },
];

// Combine all events
export const allCalendarEvents: CalendarEvent[] = [
  ...taskEvents,
  ...sprintEvents,
  ...availabilityEvents,
  ...meetingEvents,
  ...recurringEvents,
  ...googleCalendarEvents,
];

// Event type filters - matching the wireframe image
export const eventTypeFilters = [
  { type: 'task', label: 'Task deadlines and milestones', color: '#4A90E2', icon: '📅' },
  { type: 'sprint', label: 'Sprint timelines', color: '#9C27B0', icon: '📅' },
  { type: 'availability', label: 'Team availability', color: '#95A5A6', icon: '📅' },
  { type: 'meeting', label: 'Meeting schedules', color: '#00C853', icon: '📅' },
  { type: 'recurring', label: 'Recurring events', color: '#26A69A', icon: '📅' },
  { type: 'milestone', label: 'Integration with Google Calendar', color: '#4285F4', icon: '📅' },
];
